/* eslint-disable @typescript-eslint/ban-types */
import { Box, Typography, TextField, Button as MUIButton } from '@mui/material';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import CropOriginalOutlinedIcon from '@mui/icons-material/CropOriginalOutlined';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import Button from '@ui-elements/Button';
import { useState, DragEvent, ChangeEvent } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';
import BasicModal from '~/components/ui-parts/BasicModal';
import { FONT_SIZES, PALETTE } from '~/assets/styles/base';
import { uploadImage } from '~/apiTs';
import { useAppDispatch } from '~/App/hooks';
import { UploadImageResponse } from '~/common/types/api/users';
import { GMOASK_STATUS } from '~/constants/api';

const CustomModalHeader = styled(Box)`
  padding: 30px 51px 24px 51px;
`;

const StyledIcon = styled(WarningRoundedIcon)`
  font-size: 2.4rem;
  margin-right: 6px;
`;

const Caution = styled.div`
  display: flex;
  align-items: center;
  font-size: ${FONT_SIZES.body3};
  color: ${PALETTE.error.main};
  margin-top: 12px;
  width: 370px;
  line-height: 1.5em;
`;

type ImageUploadContainerProps = {
  $isDragActive: boolean;
  $isDataPreview: boolean;
};
const ImageUploadContainer = styled.div<ImageUploadContainerProps>`
  height: ${(props) => (props.$isDataPreview ? '181px' : '226px')};
  background: ${(props) => (props.$isDragActive ? PALETTE.primary.subButton : PALETTE.blue.main)};
  text-align: center;
  padding-top: 70px;
`;

const UploadControl = styled.div`
  width: 350px;
  margin-top: 50px;
  margin-bottom: -25px;
`;

const UrlTextField = styled(TextField)`
  width: 640px;
  height: 48px;
  margin-top: 20px;

  input[type='url'] {
    padding: 12px 20px;
    font-size: 16px;
    border: 1px solid ${PALETTE.border.frame};
  }
`;

const StyledImg = styled.img`
  max-height: 170px;
`;

const ImagePreviewContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 10px;
  margin-top: 10px;
  gap: 10px;
  flex-wrap: wrap;
  max-height: 170px;
`;

type Props = {
  isOpen: boolean;
  handleClose: React.MouseEventHandler<HTMLButtonElement>;
  handleSetting: Function;
};

const ImageUploadModal: React.FC<Props> = ({
  isOpen,
  handleClose,
  handleSetting,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const [isImageUploadType, setIsImageUploadType] = useState(true);
  const [activeUploadBtn, setActiveUploadBtn] = useState(true);
  const [labelValue, setLabelValue] = useState('');
  const [dataFormat, setDataFormat] = useState('');
  const [dataImage, setDataImage] = useState('');

  const [modalPreviewData, setModalPreviewData] = useState<JSX.Element | null>(null);

  const IMAGE_FORMAT = ['jpg', 'jpeg', 'png'];

  const handleSettingClick = async () => {
    if (isImageUploadType && dataImage) {
      // Call API
      const { payload: response } = (await dispatch(
        uploadImage({
          imageData: dataImage,
          format: dataFormat,
        })
      )) as unknown as PayloadAction<
        UploadImageResponse,
        string,
        { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
        never
      >;

      if (response.status === GMOASK_STATUS.OK) {
        const imgDataForLabel = <img src={response.result.imageUrl} alt="label" />;
        handleSetting(imgDataForLabel);
      }
    } else if (!isImageUploadType && labelValue) {
      const imgDataForLabel = <img src={labelValue} alt="label" />;
      handleSetting(imgDataForLabel);
    } else {
      handleSetting(undefined);
    }
  };

  const modalTitle = (
    <CustomModalHeader>
      <Typography variant="title1" sx={{ display: 'flex', alignItems: 'center', fontSize: '24px' }}>
        <CropOriginalOutlinedIcon sx={{ fontSize: '24px' }} />
        {t('registration.imageUploadModal.title')}
      </Typography>
      <Caution>
        <StyledIcon />
        {t('registration.imageUploadModal.headerNote')}
      </Caution>

      <UploadControl>
        <Button
          data-testid="image-upload-tab"
          sx={{
            color: '#032B4D',
            background: isImageUploadType ? PALETTE.blue.main : 'transparent',
            width: '200px',
          }}
          variant="text"
          onClick={() => {
            if (!isImageUploadType) {
              setIsImageUploadType(true);
              setModalPreviewData(null);
            }
          }}
        >
          {t('registration.imageUploadModal.upload')}
        </Button>
        <Button
          data-testid="url-upload-tab"
          sx={{
            width: '140px',
            color: '#032B4D',
            background: !isImageUploadType ? PALETTE.blue.main : 'transparent',
          }}
          variant="text"
          onClick={() => {
            if (isImageUploadType) {
              setIsImageUploadType(false);
              setModalPreviewData(null);
              setActiveUploadBtn(true);
            }
          }}
        >
          {t('registration.imageUploadModal.URL')}
        </Button>
      </UploadControl>
    </CustomModalHeader>
  );

  const settingButton = (
    <div key="ModalFooterInner">
      <Button
        variant="contained"
        onClick={handleSettingClick}
        colorType={PALETTE.primary.main}
        width="110px"
        dataTestId="image-upload-setting"
        disabled={!activeUploadBtn}
        sx={{ marginTop: '40px', marginBottom: '40px' }}
      >
        {t('appCommon.setting')}
      </Button>
    </div>
  );

  const convertBase64 = (file: File) =>
    new Promise<string>((resolve) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        if (typeof fileReader.result === 'string') {
          resolve(fileReader.result);
        }
      };
    });

  const updateImageData = async (file: File) => {
    const newDataFormat = file.type.split('image/')[1];
    setDataFormat(newDataFormat);

    const newImageData = await convertBase64(file);
    setDataImage(newImageData.replace(`data:image/${newDataFormat};base64,`, ''));

    const dataPreview = IMAGE_FORMAT.includes(newDataFormat) ? URL.createObjectURL(file) : '';
    setModalPreviewData(<img src={dataPreview} alt="GMO Ask Data" />);

    if (newDataFormat === undefined || (!IMAGE_FORMAT.includes(newDataFormat) && !!newImageData)) {
      setActiveUploadBtn(false);
    } else {
      setActiveUploadBtn(true);
    }
  };

  const handleUpload = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      updateImageData(event.target.files[0]);
    }
  };

  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      updateImageData(event.dataTransfer.files[0]);
    }
  };

  return (
    <BasicModal
      width="880px"
      open={isOpen}
      titleProps={{
        customModalTitle: modalTitle,
      }}
      handleClose={handleClose}
      dataTestId="image-upload-modal"
      buttons={[settingButton]}
      aboveButtons={
        <div>
          {!activeUploadBtn && (
            <Typography sx={{ fontSize: '16px', color: 'red', textAlign: 'center' }}>
              {t('registration.imageUploadModal.formatWarning')}
            </Typography>
          )}
          {modalPreviewData && (
            <ImagePreviewContainer data-testid="image-preview-container">
              <StyledImg src={modalPreviewData.props.src} />
            </ImagePreviewContainer>
          )}
        </div>
      }
    >
      <ImageUploadContainer
        data-testid="image-upload-container"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        $isDragActive={dragActive}
        $isDataPreview={modalPreviewData !== null}
      >
        {isImageUploadType ? (
          <>
            <Typography sx={{ fontSize: '16px' }}>
              {t('registration.imageUploadModal.uploadInstruction')}
            </Typography>

            <MUIButton component="label" variant="text" sx={{ fontSize: '18px' }}>
              {t('registration.imageUploadModal.ref')}
              <input
                data-testid="browse-data-button"
                type="file"
                accept="image/*"
                onChange={handleUpload}
                hidden
              />
            </MUIButton>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '16px' }}>
              {t('registration.imageUploadModal.urlInstruction')}
            </Typography>

            <UrlTextField
              inputProps={{ 'data-testid': 'url-component-textfield' }}
              onChange={(event) => {
                setLabelValue(event.target.value);
              }}
              onBlur={(event) => {
                setLabelValue(event.target.value);
                setModalPreviewData(<img src={event.target.value} alt="GMO Ask Data" />);
              }}
              sx={{ input: { color: '#032B4D' } }}
              type="url"
            />
          </>
        )}
      </ImageUploadContainer>
    </BasicModal>
  );
};

export default ImageUploadModal;
